import {
  PublicClientApplication,
  Configuration,
  AccountInfo,
  InteractionRequiredAuthError,
  BrowserAuthError,
  EventType,
  CacheLookupPolicy,
} from '@azure/msal-browser';
import { config } from '../config/environment';

// Centralized MSAL configuration and instance used across the partner app
const msalConfig: Configuration = {
  auth: {
    clientId: config.azure.clientId,
    authority: config.azure.authority,
    redirectUri: config.azure.redirectUri,
    postLogoutRedirectUri: config.azure.redirectUri,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    // Defense in depth for browsers with strict tracking prevention (Edge,
    // Safari, Chrome 3P-cookie restrictions) — lets MSAL state survive the
    // cookie-isolated iframe context when the silent iframe path is used.
    storeAuthStateInCookie: true,
  },
  system: {
    loggerOptions: {
      loggerCallback: config.features.enableMsalLogging ? (level, message, containsPii) => {
        if (containsPii) return;
        // eslint-disable-next-line no-console
        console.log(`MSAL ${level}: ${message}`);
      } : undefined,
    },
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

// The request used for interactive logins — mirrors AuthContext.loginRequest.
// Duplicated here (rather than imported) to avoid a circular dependency with
// AuthContext and to keep the boot-time probe self-contained.
const interactiveLoginRequest = {
  scopes: ['openid', 'profile', 'email', 'offline_access', config.azure.apiScope],
  prompt: 'select_account',
};

/**
 * Initialise MSAL, handle any pending redirect, wire up active-account
 * bookkeeping, and probe the cached account for a valid Core API access
 * token. If the cached account can't silently produce a token for the API
 * scope (stale state from an older login, or silent iframe blocked by
 * third-party cookie restrictions), clear the cache and force a fresh
 * interactive login via redirect.
 *
 * Called once from App.tsx before rendering the React tree.
 */
export async function initialiseMsal(): Promise<void> {
  await msalInstance.initialize();

  const redirectResponse = await msalInstance.handleRedirectPromise().catch((err) => {
    return null;
  });

  if (redirectResponse?.account) {
    const chosen = redirectResponse.account;
    msalInstance.setActiveAccount(chosen);
    for (const other of msalInstance.getAllAccounts()) {
      if (other.homeAccountId !== chosen.homeAccountId) {
        await msalInstance.clearCache({ account: other });
      }
    }
    msalInstance.setActiveAccount(chosen);
  }

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as { account: AccountInfo };
      msalInstance.setActiveAccount(payload.account);
    }
  });

  // Skip the probe in dev-mode (authentication is bypassed entirely there).
  if (config.features.devMode) return;

  const activeAccount = msalInstance.getActiveAccount();
  if (!activeAccount) return;

  try {
    await msalInstance.acquireTokenSilent({
      scopes: [config.azure.apiScope],
      account: activeAccount,
      // Never resort to the hidden iframe — it takes 10s to time out in
      // browsers that block 3p cookies on login.microsoftonline.com, leaving
      // the app blank on every launch for stale-state users.
      // AccessTokenAndRefreshToken restricts silent acquisition to the local
      // cache and the refresh-token endpoint (plain fetch). If neither works
      // it throws quickly and we fall straight through to loginRedirect.
      cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
    });
    // Cached token (or refresh-token fetch) works — proceed to render.
  } catch (err) {
    console.warn(
      '[MSAL] Cached account cannot silently acquire Core API scope — forcing fresh login.',
      err,
    );
    try {
      await msalInstance.clearCache();
    } catch (clearErr) {
      console.warn('[MSAL] clearCache failed', clearErr);
    }
    await msalInstance.loginRedirect(interactiveLoginRequest);
    // loginRedirect navigates away; block so the app does not render a
    // half-authenticated UI in the meantime.
    await new Promise(() => {});
  }
}

export function currentAccount(): AccountInfo | null {
  return msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
}

// Ensure we can fetch a fresh Microsoft token for the Core API scope
export async function ensureFreshMicrosoftToken(): Promise<string> {
  const account = currentAccount();
  if (!account) {
    throw new Error('Not authenticated');
  }

  const request = {
    scopes: [config.azure.apiScope],
    account,
  };

  try {
    const response = await msalInstance.acquireTokenSilent(request);
    return response.accessToken;
  } catch (err) {
    // Fall back to interactive on:
    // - InteractionRequiredAuthError (consent / MFA / expired refresh token)
    // - BrowserAuthError monitor_window_timeout (silent iframe blocked by 3p
    //   cookie restrictions)
    // - Stale account state from older logins missing the API scope
    const isIframeTimeout =
      err instanceof BrowserAuthError && err.errorCode === 'monitor_window_timeout';
    if (err instanceof InteractionRequiredAuthError || isIframeTimeout) {
      // Use redirect rather than popup so Windows/Edge users aren't blocked by
      // popup blockers and get a clean reauth flow.
      await msalInstance.acquireTokenRedirect(request);
      // acquireTokenRedirect navigates away; throw to satisfy types.
      throw err;
    }
    throw err;
  }
}
